import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useGestor } from '@/contexts/GestorContext'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { VW_ORCAMENTO_V2, num, type OrcamentoV2Linha } from '@/lib/gestor-orcamento-v2'

export default function GestorDetalhamentoPage() {
  const { areaSelecionada, anoSelecionado } = useGestor()
  const [data, setData] = useState<OrcamentoV2Linha[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  const fetchData = useCallback(async () => {
    if (!anoSelecionado || !areaSelecionada) return

    setLoading(true)
    try {
      const { data: viewData, error } = await supabase
        .from(VW_ORCAMENTO_V2)
        .select('*')
        .eq('ano', parseInt(anoSelecionado, 10))
        .eq('centro_resultado', areaSelecionada)
        .order('tipo_receita_despesa', { ascending: true })

      if (error) throw error
      setData((viewData as OrcamentoV2Linha[]) || [])
    } catch (error) {
      console.error('Erro ao carregar detalhamento:', error)
      toast.error('Erro ao carregar o detalhamento.')
    } finally {
      setLoading(false)
    }
  }, [anoSelecionado, areaSelecionada])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

  const filtered = data.filter((item) => {
    const q = searchTerm.toLowerCase()
    if (!q) return true
    return (
      String(item.tipo_receita_despesa || '').toLowerCase().includes(q) ||
      String(item.classificacao_receita_despesa || '').toLowerCase().includes(q) ||
      String(item.subtipo_despesa || '').toLowerCase().includes(q)
    )
  })

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#131E29]/20 border-t-orange-500 border-r-[#131E29]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Card className="border-none shadow-sm bg-white">
        <CardHeader className="pb-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-base font-semibold text-slate-800">Valores por linha de despesa</CardTitle>
            <CardDescription className="mt-1">
              Role a tabela na horizontal se precisar. Colunas com valores orçados, comprometidos, pagos e saldos.
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="search"
              placeholder="Buscar por tipo, classificação ou subtipo..."
              className="w-full bg-[#F8F9FA] border border-slate-200/80 pl-9 focus-visible:ring-orange-500 h-9 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
                <TableRow className="hover:bg-transparent border-slate-100">
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap py-3 pl-4">
                    Macro
                  </TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Classificação</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Subtipo</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Orçado</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Comprometido</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Pago</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Verba adic.</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Saldo disp.</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Sobra</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">Mov. saída</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap pr-4">
                    Mov. entrada
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="h-24 text-center text-slate-500">
                      Nenhuma linha encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item, index) => (
                    <TableRow key={`${item.tipo_receita_despesa}-${item.classificacao_receita_despesa}-${item.subtipo_despesa}-${index}`} className="border-slate-50 hover:bg-slate-50/50">
                      <TableCell className="py-3 pl-4 text-sm text-slate-800 max-w-[140px] truncate" title={item.tipo_receita_despesa || ''}>
                        {item.tipo_receita_despesa || '—'}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-slate-600 max-w-[140px] truncate" title={item.classificacao_receita_despesa || ''}>
                        {item.classificacao_receita_despesa || '—'}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-slate-600 max-w-[160px] truncate" title={item.subtipo_despesa || ''}>
                        {item.subtipo_despesa || '—'}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-right font-medium whitespace-nowrap">
                        {formatCurrency(num(item.valor_total_orcado))}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-right whitespace-nowrap">
                        {formatCurrency(num(item.valor_comprometido))}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-right whitespace-nowrap">
                        {formatCurrency(num(item.valor_pago))}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-right whitespace-nowrap">
                        {formatCurrency(num(item.valor_verba_adicional))}
                      </TableCell>
                      <TableCell
                        className={`py-3 text-sm text-right font-medium whitespace-nowrap ${
                          num(item.valor_saldo_disponivel) < 0 ? 'text-red-600' : 'text-orange-800'
                        }`}
                      >
                        {formatCurrency(num(item.valor_saldo_disponivel))}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-right whitespace-nowrap text-slate-700">
                        {formatCurrency(num(item.valor_sobra))}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-right whitespace-nowrap text-slate-600">
                        {formatCurrency(num(item.valor_movimentado_saida))}
                      </TableCell>
                      <TableCell className="py-3 pr-4 text-sm text-right whitespace-nowrap text-slate-600">
                        {formatCurrency(num(item.valor_movimentado_entrada))}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
